import { useCallback, useEffect, useRef, useState } from 'react';
import salesforceTasksService from 'services/salesforceTasksService';

const PAGE_SIZE = 25;

const useSalesforceWorkQueue = (filter = 'open', sortModel = []) => {
	const [tasks, setTasks] = useState([]);
	const [totalCount, setTotalCount] = useState(null);
	const [nextCursor, setNextCursor] = useState(null);
	const [hasMore, setHasMore] = useState(true);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [loadMoreError, setLoadMoreError] = useState(null);
	const [refreshing, setRefreshing] = useState(false);
	const [error, setError] = useState(null);
	const [refreshError, setRefreshError] = useState(null);

	// Track the loaded record count for sort changes
	const loadedCountRef = useRef(0);
	const tasksRef = useRef([]);

	// Prevent stale requests from updating the current queue
	const requestIdRef = useRef(0);
	const loadingMoreRef = useRef(false);
	// Prevents automatic pagination retries after an error
	const loadMoreErrorRef = useRef(false);
	// Stores the current pagination cursor
	const cursorRef = useRef(null);
	const hasMoreRef = useRef(true);
	const fetchingFirstPageRef = useRef(true);

	// Track the last successful query and initial load
	const hasLoadedRef = useRef(false);
	const previousQueryRef = useRef(null);

	// The API supports one sort field and direction.
	const sortBy = sortModel?.[0]?.field ?? 'priority';
	const sortDirection = sortModel?.[0]?.sort ?? 'asc';

	const loadFirstPage = useCallback(async () => {
		// Invalidate previous requests so stale responses cannot overwrite current data
		const requestId = ++requestIdRef.current; // Increment the request ID to track the latest API request
		const isInitialLoad = !hasLoadedRef.current;

		const previousQuery = previousQueryRef.current;

		const currentQuery = {
			filter,
			sortBy,
			sortDirection,
		};

		// Switching open/all changes the dataset
		const filterChanged =
			previousQuery !== null && previousQuery.filter !== filter;

		// Sorting changes the order, but not the dataset
		const sortChanged =
			previousQuery !== null &&
			!filterChanged &&
			(previousQuery.sortBy !== sortBy ||
				previousQuery.sortDirection !== sortDirection);

		const queryChanged = filterChanged || sortChanged;
		const isRefresh = !isInitialLoad && !queryChanged;

		// Keep the same number of loaded records when sorting
		// A filter change or refresh starts with PAGE_SIZE
		const limit = sortChanged
			? Math.max(PAGE_SIZE, loadedCountRef.current)
			: PAGE_SIZE;

		previousQueryRef.current = currentQuery;

		// Invalidate the previous cursor and pagination requests
		fetchingFirstPageRef.current = true;
		loadingMoreRef.current = false;
		loadMoreErrorRef.current = false;
		cursorRef.current = null;
		hasMoreRef.current = false;

		setNextCursor(null);
		setHasMore(false);
		setLoadingMore(false);
		setLoadMoreError(null);
		setError(null);
		setRefreshError(null);

		// Reset the loaded count when the dataset changes
		if (filterChanged) {
			tasksRef.current = [];
			loadedCountRef.current = 0;
		}

		// Clear old rows when switching views or sort order
		if (queryChanged) {
			setTasks([]);
			setTotalCount(null);
		}

		// Preserve existing rows during a same-query refresh
		if (isRefresh) {
			setLoading(false);
			setRefreshing(true);
		} else {
			setLoading(true);
			setRefreshing(false);
		}

		try {
			const response = await salesforceTasksService.getWorkQueueTasks(
				limit,
				null,
				filter,
				sortBy,
				sortDirection
			);

			if (requestId !== requestIdRef.current) return;

			const records = response.tasks ?? [];
			const cursor = response.next_cursor ?? null;
			const more = Boolean(response.has_more && cursor);

			cursorRef.current = cursor;
			hasMoreRef.current = more;

			tasksRef.current = records;
			loadedCountRef.current = records.length;

			setTasks(records);
			setTotalCount(response.total_count ?? null);
			setNextCursor(cursor);
			setHasMore(more);
		} catch (err) {
			if (requestId !== requestIdRef.current) return;

			const message = err?.message ?? 'Unable to load Work Queue tasks';

			if (isRefresh) {
				// Preserve existing rows and report the refresh failure separately
				setRefreshError(message);
			} else {
				setError(message);
			}
		} finally {
			if (requestId === requestIdRef.current) {
				hasLoadedRef.current = true;
				fetchingFirstPageRef.current = false;
				loadingMoreRef.current = false;

				setLoading(false);
				setLoadingMore(false);
				setRefreshing(false);
			}
		}
	}, [filter, sortBy, sortDirection]);

	// Fetch the first page on mount and whenever the query changes
	useEffect(() => {
		loadFirstPage();

		return () => {
			requestIdRef.current += 1;
		};
	}, [loadFirstPage]);

	const loadMore = useCallback(async () => {
		// Prevent duplicate and overlapping pagination requests
		if (
			fetchingFirstPageRef.current ||
			loadingMoreRef.current ||
			loadMoreErrorRef.current ||
			!hasMoreRef.current ||
			!cursorRef.current
		) {
			return;
		}

		loadingMoreRef.current = true;
		setLoadingMore(true);
		setLoadMoreError(null);

		const requestId = requestIdRef.current;
		const cursor = cursorRef.current;

		try {
			const response = await salesforceTasksService.getWorkQueueTasks(
				PAGE_SIZE,
				cursor,
				filter,
				sortBy,
				sortDirection
			);

			if (requestId !== requestIdRef.current) return;

			const records = response.tasks ?? [];
			const newCursor = response.next_cursor ?? null;
			const more = Boolean(response.has_more && newCursor);

			cursorRef.current = newCursor;
			hasMoreRef.current = more;

			// Preserve backend ordering and avoid duplicate task IDs
			const existingIds = new Set(tasksRef.current.map((task) => task.id));

			const uniqueRecords = records.filter((task) => {
				if (existingIds.has(task.id)) return false;

				existingIds.add(task.id);
				return true;
			});

			const updatedTasks = [...tasksRef.current, ...uniqueRecords];

			tasksRef.current = updatedTasks;
			loadedCountRef.current = updatedTasks.length;

			setTasks(updatedTasks);
			setNextCursor(newCursor);
			setHasMore(more);
		} catch (err) {
			if (requestId !== requestIdRef.current) return;

			// Keep loaded tasks and stop automatic pagination retries
			loadMoreErrorRef.current = true;
			setLoadMoreError(err?.message ?? 'Unable to load more tasks');
		} finally {
			if (requestId === requestIdRef.current) {
				loadingMoreRef.current = false;
				setLoadingMore(false);
			}
		}
	}, [filter, sortBy, sortDirection]);

	return {
		tasks,
		totalCount,
		nextCursor,
		loading,
		loadingMore,
		loadMoreError,
		refreshing,
		refreshError,
		error,
		hasMore,
		loadMore,
		refetch: loadFirstPage,
	};
};

export default useSalesforceWorkQueue;
